<template>
  <WithBackdrop
    :backdrop="data?.fields?.backdrop"
    :class="[$style.config, $style.wrap]"
  >
    <header v-if="data?.fields?.header">
      <BaseHeading size="h3" as="h2" :class="$style.header">
        {{ data?.fields?.header }}
      </BaseHeading>
    </header>
    <div :class="$style.container">
      <template v-if="computedHighlights?.length">
        <article
          v-for="h in computedHighlights"
          :key="h?.sys?.id"
          :class="$style.card"
        >
          <div :class="$style.cardMedia">
            <BasePicture
              v-if="h?.fields?.media?.fields?.file?.url"
              :src="`${h?.fields?.media?.fields?.file?.url}?w=500`"
              :img-class="$style.cardImg"
              alt=""
            />
          </div>
          <div :class="$style.cardContent">
            <MarkedParser :md="h?.fields?.copy" :class="$style.cardBody" />
            <div v-for="a in h?.fields?.ctas" :key="a?.sys?.id">
              <CallToAction :cta="a" :class="$style.cardAction" />
            </div>
          </div>
        </article>
      </template>
    </div>
  </WithBackdrop>
</template>

<script setup lang="ts">
import type { TypeContentBlockWithoutUnresolvableLinksResponse } from '~/types/contentful'

const props = defineProps<{
  data: TypeContentBlockWithoutUnresolvableLinksResponse
}>()

const computedHighlights = computed(() =>
  props.data?.fields?.references?.filter(instanceOfCard),
)
</script>

<style lang="scss" module>
.config {
  --container-max-width: 80vw;

  --wrap-padding--base: 30px 0;
  --wrap-padding--sm: 40px 15px;
  --wrap-padding--md: 60px 15px 90px;
  --wrap-padding: var(--wrap-padding--base);

  --header-font-color: var(--black);
  --header-font-weight: 900;

  --card-gap: 30px;

  --card-media-flex: 0 0 100%;
  --card-content-flex: 0 0 100%;

  --highlights-grid-cta-color: var(--theme-link-color, var(--white));
  --highlights-grid-cta-color--hover: var(
    --theme-link-color--active,
    var(--white)
  );

  @include media('sm') {
    --wrap-padding: var(--wrap-padding--sm);
    --header-font-size: 60px;
    --card-media-flex: 0 0 50%;
    --card-content-flex: 0 0 50%;
    --card-gap: 0px;
  }

  @include media('md') {
    --wrap-padding: var(--wrap-padding--md);
    --container-max-width: 900px;
  }
}

.wrap {
  padding: var(--wrap-padding);
}

.container {
  display: grid;
  gap: var(--card-gap);
  max-width: var(--container-max-width);
  margin: 0 auto;
}

.header.header {
  color: var(--header-font-color);
  font-weight: var(--header-font-weight);
  line-height: 1;
  text-align: center;
  margin-bottom: 1em;
  padding: 0 0.5em;
}

.card {
  color: var(--black);
  display: flex;
  flex-wrap: wrap;

  &:nth-child(even) {
    flex-direction: row-reverse;
  }
}

.cardMedia {
  flex: var(--card-media-flex);
}

.cardImg {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.cardContent {
  background: #fff;
  @include fluid-type(18px, 21px);

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  flex: var(--card-content-flex);

  padding: 2em;

  .cardHeader {
    font-weight: bold;
    margin-bottom: 0.75em;
  }

  .cardBody {
    font-size: 0.7em;
    margin-bottom: 1.5em;
  }

  .cardAction.cardAction {
    color: var(--highlights-grid-cta-color);

    &:active {
      color: var(--theme-link-color--active, #b93b0d);
    }
  }
}

.cardBody {
  font-size: 0.7em;
  margin-bottom: 1.5em;

  h3 {
    font-size: to-rem(24px);
    margin-bottom: 18px;
  }
}
</style>
